import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";

const teamMembers = [
  {
    name: "Michael Rodriguez",
    position: "Founder & CEO",
    image: "/images/team-michael.jpg",
    bio: "With over 25 years in the construction industry, Michael founded Rough Framing Co. with a vision to deliver exceptional quality and craftsmanship. His expertise spans residential and commercial projects across the region.",
    experience: "25+ Years",
    specialties: ["Structural Engineering", "Project Management", "Business Development"]
  },
  {
    name: "Sarah Johnson",
    position: "Operations Manager",
    image: "/images/team-sarah.jpg",
    bio: "Sarah oversees all day-to-day operations, ensuring projects run smoothly and efficiently. Her attention to detail and organizational skills keep our team on schedule and within budget.",
    experience: "15+ Years",
    specialties: ["Operations Management", "Quality Control", "Team Leadership"]
  },
  {
    name: "David Chen",
    position: "Lead Foreman",
    image: "/images/team-david.jpg",
    bio: "David leads our field crews with precision and expertise. His hands-on approach and deep knowledge of framing techniques ensure every project meets our high standards.",
    experience: "20+ Years",
    specialties: ["Framing Techniques", "Crew Management", "Safety Protocols"]
  },
  {
    name: "Jennifer Martinez",
    position: "Project Coordinator",
    image: "/images/team-jennifer.jpg",
    bio: "Jennifer coordinates project timelines, materials, and client communications. Her proactive approach ensures seamless project delivery from start to finish.",
    experience: "12+ Years",
    specialties: ["Project Coordination", "Client Relations", "Material Planning"]
  }
];

const values = [
  {
    icon: "🏗️",
    title: "Quality Craftsmanship",
    description: "We never compromise on quality. Every beam, every joint, every connection is built to last and exceed industry standards."
  },
  {
    icon: "⏰",
    title: "Reliability",
    description: "When we commit to a timeline, we deliver. Our reputation is built on completing projects on time, every time."
  },
  {
    icon: "🤝",
    title: "Integrity",
    description: "Honest communication, transparent pricing, and ethical business practices are the foundation of every client relationship."
  },
  {
    icon: "🔧",
    title: "Innovation",
    description: "We stay current with the latest techniques, materials, and technologies to deliver the best possible results."
  }
];

const achievements = [
  {
    year: "2024",
    title: "Framing Excellence Award",
    description: "Recognized by the Orange County Builders Association for outstanding rough framing quality"
  },
  {
    year: "2023",
    title: "Safety Excellence Award",
    description: "Zero safety incidents for four consecutive years"
  },
  {
    year: "2022",
    title: "200+ Framing Projects",
    description: "Milestone achievement of completing over 200 successful rough framing projects"
  },
  {
    year: "2021",
    title: "Cost-Effective Solutions Award",
    description: "Recognized for delivering quality framing at competitive prices"
  }
];

const stats = [
  { number: "200+", label: "Framing Projects Completed" },
  { number: "5+", label: "Years Specialized Experience" },
  { number: "20+", label: "Expert Team Members" },
  { number: "100%", label: "Client Satisfaction" }
];

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-black">
      <Helmet>
        <title>About Us | Low Cost Framing by Woodhenge Builders | Our Story & Team</title>
        <meta name="description" content="Learn about Low Cost Framing's expertise in rough framing, our expert team, company values, and commitment to quality craftsmanship. Backed by Woodhenge Builders' 20+ years of experience." />
        <meta name="keywords" content="rough framing contractor, framing services, Orange County framing, Woodhenge Builders, construction framing" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative h-[60vh] overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: "url(/images/about-hero.jpg)" }}
        >
          <div className="absolute inset-0 bg-black/60"></div>
        </div>
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white max-w-4xl mx-auto px-6">
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              About Low Cost Framing
            </h1>
            <p className="text-xl md:text-2xl mb-8 opacity-90 font-light text-amber-100">
              Specializing in cost-effective, high-quality rough framing since 2019
            </p>
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Work With Us
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Company Story */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-6xl font-bold text-white mb-8 tracking-tight">
                Our Story
              </h2>
              <div className="space-y-6 text-gray-300 text-lg leading-relaxed">
                <p>
                  Low Cost Framing was established in 2019 as a specialized division of Woodhenge Builders, 
                  focusing exclusively on rough framing services. Our mission is to deliver exceptional 
                  framing quality at competitive prices, making professional framing accessible to more projects.
                </p>
                <p>
                  Backed by Woodhenge Builders' 20+ years of construction expertise, we bring the same 
                  attention to detail and quality craftsmanship to every framing project. Our specialized 
                  focus allows us to optimize processes and pass savings to our clients while maintaining 
                  the highest standards of workmanship.
                </p>
                <p>
                  Today, we're proud to serve homeowners, builders, and developers across Orange County 
                  and surrounding areas, bringing reliable, cost-effective rough framing solutions to 
                  projects of all sizes.
                </p>
              </div>
            </div>
            <div className="relative">
              <div 
                className="h-96 rounded-2xl bg-cover bg-center bg-no-repeat shadow-2xl"
                style={{ backgroundImage: "url(/images/company-story.jpg)" }}
              >
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent rounded-2xl"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-6xl font-bold text-amber-400 mb-4">
                  {stat.number}
                </div>
                <div className="text-gray-300 text-lg font-semibold">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Our Values
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              The principles that guide our rough framing excellence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl p-10 shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 border border-gray-700 hover:border-amber-600/30 group"
              >
                <div className="text-5xl mb-6 group-hover:scale-110 transition-transform duration-300">
                  {value.icon}
                </div>
                <h3 className="text-2xl font-bold text-amber-400 mb-4 group-hover:text-amber-300 transition-colors">
                  {value.title}
                </h3>
                <p className="text-gray-300 leading-relaxed text-lg">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Meet Our Team
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Expert framing professionals backed by Woodhenge Builders' construction expertise
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 border border-gray-700 hover:border-amber-600/30 overflow-hidden group"
              >
                <div className="h-64 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url(${member.image})` }}>
                  <div className="h-full bg-gradient-to-t from-black/60 to-transparent"></div>
                </div>
                <div className="p-8">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-2xl font-bold text-amber-400 group-hover:text-amber-300 transition-colors">
                        {member.name}
                      </h3>
                      <p className="text-gray-400 text-lg">{member.position}</p>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-400">Experience</div>
                      <div className="text-lg font-bold text-white">{member.experience}</div>
                    </div>
                  </div>
                  <p className="text-gray-300 mb-6 leading-relaxed">
                    {member.bio}
                  </p>
                  <div>
                    <h4 className="text-sm font-semibold text-amber-400 mb-3">Specialties:</h4>
                    <div className="flex flex-wrap gap-2">
                      {member.specialties.map((specialty, specialtyIndex) => (
                        <span
                          key={specialtyIndex}
                          className="bg-gray-700 text-gray-300 px-3 py-1 rounded-full text-sm border border-gray-600"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-24 bg-gray-900">
        <div className="container mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
              Awards & Recognition
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              Industry recognition for our commitment to framing excellence
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="bg-gray-800 rounded-2xl p-8 shadow-2xl hover:shadow-amber-900/20 transition-all duration-500 transform hover:-translate-y-2 border border-gray-700 hover:border-amber-600/30 group"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="text-3xl font-bold text-amber-400">{achievement.year}</div>
                  <div className="bg-amber-800 w-12 h-12 rounded-full flex items-center justify-center">
                    <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </div>
                </div>
                <h3 className="text-xl font-bold text-white mb-4 group-hover:text-amber-300 transition-colors">
                  {achievement.title}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {achievement.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 tracking-tight">
            Ready to Get Your Free Quote?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
            Join hundreds of satisfied clients who have trusted us with their rough framing projects. 
            Let's build something exceptional together with quality craftsmanship and competitive pricing.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <button className="bg-amber-800 hover:bg-amber-700 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-amber-600/50 shadow-2xl border border-amber-600">
                Get Free Quote
              </button>
            </Link>
            <a href="tel:+1234567890">
              <button className="bg-transparent hover:bg-gray-800 text-white font-bold py-4 px-10 rounded-lg text-lg transition-all duration-300 border-2 border-amber-600 hover:border-amber-500 focus:outline-none focus:ring-4 focus:ring-amber-600/50">
                Call (123) 456-7890
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 